import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { and } from 'ember-truth-helpers';
import GeneralButtonsButtonDayLiveshop from 'lumen/components/general/buttons/button-day-liveshop';
import GeneralTooltipsTooltipEvents from 'lumen/components/general/tooltips/tooltip-events';
import HotelRatesTablesCellsTdBase from 'lumen/components/hotel/rates/tables/cells/td-base';
import highlightElement from 'lumen/modifiers/highlight-element';

export default class HotelRatesTablesCellsTdDate extends HotelRatesTablesCellsTdBase {
  @service declare access: Services['access'];
  @service declare liveupdate: Services['liveupdate'];

  @tracked isDateCellHovered = false;

  highlightElement = highlightElement;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dayId() {
    const date = this.day?.date?.format('YYYY-MM-DD');

    return date ? `smart-insight-day-${date}` : undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHolidaysOrEvents() {
    if (!this.access.hasFeature('app.events')) {
      return false;
    }

    return this.day.holidays.length || this.day.calendarEvents.length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get holidays() {
    return [...this.day.holidays];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDayLiveupdateLimitReached() {
    return this.liveupdate?.isDayLiveupdateLimitReached;
  }

  get isDayLiveupdateTriggerAllowed(): boolean {
    return (
      !this.isUpdating &&
      Boolean(this.args.column['hasDayLiveShop']) &&
      Boolean(this.args.column['isOtaSupported']) &&
      this.day?.date.isSameOrAfter(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDayLiveupdateTriggerVisible() {
    return this.isDateCellHovered && !this.isPartialLiveupdateOngoing;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isUpdating() {
    return this.liveupdate.isRatesUpdating(
      this.period.fromDate,
      this.day?.date,
      this.period.rates,
    );
  }

  @action
  hideDayLiveupdateTrigger(): void {
    this.isDateCellHovered = false;
  }

  @action
  showDayLiveupdateTrigger(): void {
    this.isDateCellHovered = true;
  }

  @action
  startDayLiveupdate(): void {
    if (this.isDayLiveupdateLimitReached) {
      return;
    }

    this.liveupdate.startLiveRateShop(false, null, null, this.formattedDate);
  }

  <template>
    <div
      class="flex items-center justify-between gap-200
        {{if this.isPast 'td-past'}}"
      role="tooltip"
      {{on "mouseenter" this.showDayLiveupdateTrigger}}
      {{on "mouseleave" this.hideDayLiveupdateTrigger}}
      {{this.highlightElement id=this.dayId highlightParentElement=true}}
    >
      <div
        class="text-200--medium text-neutral-emphasis flex shrink items-center justify-start gap-100 truncate"
      >
        <p
          class="truncate {{if this.isToday 'td-today-date'}}"
        >{{this.formattedPrettyDate}}</p>

        {{#if this.hasHolidaysOrEvents}}
          <PrismIcon @name="calendar-accepted" @size="small" />
          <EmberTooltip
            @delay={{25}}
            @side="right"
            @tooltipClass="base-tooltip day-detail-tooltip"
          >
            <GeneralTooltipsTooltipEvents
              @events={{this.day.calendarEvents}}
              @holidays={{this.holidays}}
              @mergeCountryFlags={{true}}
              @title="menu.events"
            />
          </EmberTooltip>
        {{/if}}
      </div>

      <GeneralButtonsButtonDayLiveshop
        @isDayLiveupdateLimitReached={{this.isDayLiveupdateLimitReached}}
        @isUpdating={{this.isUpdating}}
        @isVisible={{and
          this.isDayLiveupdateTriggerVisible
          this.isDayLiveupdateTriggerAllowed
        }}
        @startDayLiveupdate={{this.startDayLiveupdate}}
      />
    </div>
  </template>
}
